import { useEffect, useState } from 'react';
import { API, type AdminAccessResponse } from '../api';
import { getLLAuthHeaders } from '../pages/new-designs/utils/llAuthHeaders';
import { useProgramContext } from './useProgramContext';

// What admin access the signed-in user has (GET /v3/admin/access): the whole
// org, programs they administer, teams they manage. Team managers are the
// reason this exists — neither the JWT role nor the profile's admin programs
// know about them.
//
// Routing, the sidebar and the dashboard all ask, so it's fetched once per
// sign-in (keyed by the id token) and shared.

let cached: { key: string; promise: Promise<AdminAccessResponse | null> } | null = null;

const fetchAdminAccess = (location: string): Promise<AdminAccessResponse | null> => {
  const headers = getLLAuthHeaders();
  if (!headers.idtoken || !headers.authorization) return Promise.resolve(null);
  const key = `${headers.idtoken}|${location}`;
  if (cached?.key === key) return cached.promise;
  const promise: Promise<AdminAccessResponse | null> = API.getAdminAccess({ ...headers, location })
    .then((res) => res?.data ?? null)
    // A BE without the endpoint, or a failed call: no team access, which is
    // exactly the behaviour from before managers existed. Not cached, so a
    // transient failure doesn't hide a manager's admin side for the session.
    .catch(() => {
      if (cached?.promise === promise) cached = null;
      return null;
    });
  cached = { key, promise };
  return promise;
};

export interface AdminAccessState {
  access: AdminAccessResponse | null;
  loading: boolean;
  /** Manages at least one team — and has no other admin access. */
  isTeamManagerOnly: boolean;
}

export const useAdminAccess = (): AdminAccessState => {
  const { location } = useProgramContext();
  const [access, setAccess] = useState<AdminAccessResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAdminAccess(location).then((result) => {
      if (!alive) return;
      setAccess(result);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [location]);

  const isTeamManagerOnly = Boolean(
    access && !access.orgAdmin && access.adminProgramIds.length === 0 && access.managedTeamIds.length > 0
  );
  return { access, loading, isTeamManagerOnly };
};
